import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { formatDate, formatMoney } from "../lib/format";
import { ModuleSwitcher } from "../components/ModuleSwitcher";

/** Per-module rate card key sets. Future modules declare their own keys here. */
const RATE_KEYS_BY_MODULE: Record<string, Array<{ key: string; label: string; unit: string }>> = {
  holiday: [
    { key: "day_hour",              label: "Additional Day Support",   unit: "/ hr" },
    { key: "night_hour",            label: "Additional Night Support", unit: "/ hr" },
    { key: "sleep_in",              label: "Sleep-in Shift",            unit: "/ shift" },
    { key: "staff_expense_per_day", label: "Staff Expenses",            unit: "/ day" },
  ],
  // case-by-case will land its own bespoke rate keys here when designed
};

export function Rates() {
  const qc = useQueryClient();
  const [moduleKey, setModuleKey] = useState<string>("holiday");
  const list = useQuery({
    queryKey: ["rate-card-admin", moduleKey],
    queryFn: () => api.rateCardByModule(moduleKey),
  });
  const RATE_KEYS = RATE_KEYS_BY_MODULE[moduleKey] ?? [];

  const [newRate, setNewRate] = useState<Record<string, { pence: string; effective_from: string; notes: string }>>({});

  if (list.isLoading) return <div className="text-slate-500">Loading...</div>;

  const rows: any[] = list.data?.rates ?? [];

  function getActive(key: string) {
    return rows.find((r) => r.key === key && !r.effective_to);
  }
  function getHistory(key: string) {
    return rows.filter((r) => r.key === key && r.effective_to);
  }

  async function save(key: string) {
    const draft = newRate[key];
    if (!draft) return;
    if (!draft.pence || !draft.effective_from) { alert("Need a price and an effective-from date."); return; }
    const pence = Math.round(parseFloat(draft.pence) * 100);
    if (!Number.isFinite(pence) || pence <= 0) { alert("Invalid price."); return; }
    await api.rateCardByModuleAdd(moduleKey, { key, pence, effective_from: draft.effective_from, notes: draft.notes || undefined });
    setNewRate((s) => ({ ...s, [key]: { pence: "", effective_from: "", notes: "" } }));
    await qc.invalidateQueries({ queryKey: ["rate-card-admin"] });
    await qc.invalidateQueries({ queryKey: ["rates"] });
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-semibold text-liaise-navy">Fixed rates</h1>
        <p className="text-slate-500 mt-1">
          Per-module rate cards. New rates are effective-dated: existing estimates keep the rate that applied when they were submitted.
        </p>
      </div>

      <ModuleSwitcher value={moduleKey} onChange={setModuleKey} filter="rate_card" />

      {RATE_KEYS.length === 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 text-sm text-slate-500">
          This module doesn't have a rate card defined yet. Once the module's data model is designed, declare its rate keys in <code className="text-xs bg-slate-100 px-1 rounded">RATE_KEYS_BY_MODULE</code> and they'll appear here.
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {RATE_KEYS.map(({ key, label, unit }) => {
          const active = getActive(key);
          const history = getHistory(key);
          const draft = newRate[key] ?? { pence: "", effective_from: "", notes: "" };

          return (
            <div key={key} className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
                  <div className="text-2xl font-semibold text-liaise-navy mt-1">
                    {active ? formatMoney(active.pence / 100) : "Not set"} <span className="text-sm text-slate-500 font-normal">{unit}</span>
                  </div>
                  {active?.effective_from && <div className="text-xs text-slate-500 mt-1">Effective from {formatDate(active.effective_from)}</div>}
                </div>
              </div>

              <div className="border-t border-slate-100 pt-3">
                <div className="text-xs font-medium text-slate-500 mb-2">Replace with new rate:</div>
                <div className="grid grid-cols-[1fr_1fr] gap-2">
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">New price (£)</div>
                    <input type="number" step="0.01" min="0" value={draft.pence} placeholder="0.00"
                      onChange={(e) => setNewRate((s) => ({ ...s, [key]: { ...draft, pence: e.target.value } }))}
                      className="mt-1 w-full px-3 py-2 border border-slate-200 rounded text-sm" />
                  </label>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Effective from</div>
                    <input type="date" value={draft.effective_from}
                      onChange={(e) => setNewRate((s) => ({ ...s, [key]: { ...draft, effective_from: e.target.value } }))}
                      className="mt-1 w-full px-3 py-2 border border-slate-200 rounded text-sm" />
                  </label>
                </div>
                <label className="block mt-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">Notes (optional)</div>
                  <input type="text" value={draft.notes} placeholder="e.g. FY27 uplift"
                    onChange={(e) => setNewRate((s) => ({ ...s, [key]: { ...draft, notes: e.target.value } }))}
                    className="mt-1 w-full px-3 py-2 border border-slate-200 rounded text-sm" />
                </label>
                <button onClick={() => save(key)}
                  className="mt-3 px-3 py-2 bg-liaise-navy text-white rounded text-sm font-medium">
                  Save new rate
                </button>
              </div>

              {history.length > 0 && (
                <div className="border-t border-slate-100 pt-3 text-xs text-slate-500 space-y-1">
                  <div className="font-medium">History</div>
                  {history.map((h) => (
                    <div key={h.id} className="flex justify-between">
                      <span>{formatDate(h.effective_from)} → {formatDate(h.effective_to)}</span>
                      <span>{formatMoney(h.pence / 100)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
